'use strict';

// EPUB footnote reference detection and resolution.
//
// The EPUB standard identifies footnote references with `epub:type="noteref"`
// (a space-separated token list) or `role="doc-noteref"`. Detection is
// attribute-based instead of CSS-selector-based because chapter documents are
// XHTML: a namespaced `epub:type` attribute is not reliably matchable with
// `querySelector('[epub\\:type]')` across parsers, while `getAttribute()`
// always sees the qualified name.

const DISALLOWED_TAGS = ['script', 'style', 'link', 'meta', 'iframe', 'object', 'embed', 'form', 'input', 'button'];

// Every attribute that can carry a URL, in any of the note's namespaces:
// SVG anchors spell their link `xlink:href`, which string-comparing against
// 'href' would miss.
const URL_ATTRIBUTES = ['href', 'xlink:href', 'src', 'srcset', 'data', 'action', 'formaction', 'poster'];

/** Detects javascript: URLs, tolerating the control characters HTML parsing allows inside the scheme. */
function hasUnsafeScheme(value) {
    let compacted = '';
    for (const character of value) {
        if (character.charCodeAt(0) > 0x20) compacted += character;
    }
    return compacted.toLowerCase().startsWith('javascript:');
}

/**
 * Checks whether an anchor is an EPUB footnote reference.
 * @param {Element|null} anchor
 * @returns {boolean}
 */
export function isNoterefAnchor(anchor) {
    if (!anchor || anchor.tagName?.toLowerCase() !== 'a') return false;

    const epubType = anchor.getAttribute('epub:type') || '';
    if (epubType.split(/\s+/).includes('noteref')) return true;

    const role = anchor.getAttribute('role') || '';
    return role.split(/\s+/).includes('doc-noteref');
}

/**
 * Walks up from an event target to the enclosing footnote-reference anchor.
 * The click usually lands on the `<sup>` or text node inside the anchor.
 * @param {Element|null} target
 * @returns {Element|null} The noteref anchor, or null when the target is not inside one
 */
export function findNoterefAnchor(target) {
    const anchor = target?.closest?.('a') ?? null;
    return isNoterefAnchor(anchor) ? anchor : null;
}

/**
 * Finds the live anchor an R2 click/tap report names. R2 reports the anchor
 * as the outerHTML of the gesture's nearest interactive element, and
 * reparsing that markup does not preserve the `epub:` prefix, so the report
 * only serves as a key: the live anchor is the one whose serialization is
 * the reported one.
 * @param {string|undefined} interactiveElement - The report's `interactiveElement`
 * @param {Document} contentDocument - The chapter document the report came from
 * @returns {Element|null} The anchor, or null when the report names no linked anchor of this document
 */
export function findReportedAnchor(interactiveElement, contentDocument) {
    if (!interactiveElement) return null;

    const reportedElement = new DOMParser().parseFromString(interactiveElement, 'text/html').body.children[0];
    if (reportedElement?.nodeName !== 'A') return null;

    const href = reportedElement.getAttribute('href');
    if (!href) return null;

    return (
        [...contentDocument.getElementsByTagName('a')].find(
            anchor => anchor.getAttribute('href') === href && anchor.outerHTML === interactiveElement
        ) ?? null
    );
}

/**
 * Splits a noteref's href into its file and fragment parts and decides
 * whether the note lives in the same chapter document as the marker.
 *
 * Same-file forms: `#fn1`, `chapter.xhtml#fn1` (when chapter.xhtml is the
 * current document). Anything pointing to another file is out of scope for
 * the panel. The caller lets the reader's own navigation handle the jump.
 *
 * @param {Element} anchor - The noteref anchor
 * @param {Document} contentDocument - The chapter document that owns the anchor
 * @returns {{fragment: string, isSameFile: boolean}|null} null when the href has no fragment
 */
export function resolveNoteTarget(anchor, contentDocument) {
    const href = anchor.getAttribute('href') || '';
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1 || hashIndex === href.length - 1) return null;

    const filePart = href.slice(0, hashIndex);
    const fragment = decodeURIComponent(href.slice(hashIndex + 1));
    if (!filePart) return { fragment, isSameFile: true };

    // Readium injects a <base data-readium> tag pointing at the chapter file;
    // fall back to the document URL for non-Readium contexts (tests).
    const baseHref = contentDocument.querySelector('base')?.getAttribute('href') || contentDocument.URL || '';
    const ownFileName = baseHref.split('/').pop()?.split('?')[0] || '';
    const targetFileName = filePart.split('/').pop() || '';

    return { fragment, isSameFile: targetFileName === ownFileName };
}

/**
 * Extracts the note body as sanitized HTML safe to render outside the
 * content iframe. The EPUB markup crosses into the parent document, so
 * active content and event handlers are stripped and image URLs are
 * absolutized: in the panel they would otherwise resolve against the
 * app origin instead of the chapter's base.
 * @param {Element} noteElement - The element the noteref fragment points to
 * @returns {{html: string, text: string}}
 */
export function extractNoteContent(noteElement) {
    const clone = noteElement.cloneNode(true);

    clone.querySelectorAll(DISALLOWED_TAGS.join(', ')).forEach(el => el.remove());

    const elementsToSanitize = [clone, ...clone.querySelectorAll('*')];
    for (const element of elementsToSanitize) {
        for (const attr of [...element.attributes]) {
            const name = attr.name.toLowerCase();
            if (name.startsWith('on')) {
                element.removeAttribute(attr.name);
            } else if (URL_ATTRIBUTES.includes(name) && hasUnsafeScheme(attr.value)) {
                element.removeAttribute(attr.name);
            } else if (name === 'style') {
                // EPUB inline styles carry the book's print layout, and the
                // panel imposes the reader theme instead.
                element.removeAttribute(attr.name);
            }
        }
    }

    // No link inside the note (including the backlink to the marker) may
    // navigate the parent document, in HTML or SVG spelling.
    clone.querySelectorAll('a').forEach(anchorElement => {
        anchorElement.removeAttribute('href');
        anchorElement.removeAttribute('xlink:href');
        anchorElement.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
    });

    // R2 serves chapters as blob: documents with an absolute <base>, so the
    // note's relative image URLs only resolve inside the iframe.
    const chapterBaseUrl = noteElement.ownerDocument?.baseURI;
    clone.querySelectorAll('img[src]').forEach(image => {
        image.removeAttribute('srcset');
        try {
            image.setAttribute('src', new URL(image.getAttribute('src'), chapterBaseUrl).href);
        } catch {
            image.remove();
        }
    });

    return {
        html: clone.innerHTML.trim(),
        text: (clone.textContent || '').trim(),
    };
}

/**
 * Full click-to-note resolution: detects the noteref, classifies the target
 * and, for same-file notes, extracts the sanitized content.
 * @param {Element|null} target - The click/tap event target
 * @param {Document} contentDocument - The chapter document
 * @returns {{kind: 'none'} | {kind: 'cross-file'} | {kind: 'note', label: string, html: string, text: string, anchor: Element, fragment: string}}
 */
export function resolveFootnoteClick(target, contentDocument) {
    const anchor = findNoterefAnchor(target);
    if (!anchor) return { kind: 'none' };

    const noteTarget = resolveNoteTarget(anchor, contentDocument);
    if (!noteTarget) return { kind: 'none' };
    if (!noteTarget.isSameFile) return { kind: 'cross-file' };

    const noteElement = contentDocument.getElementById(noteTarget.fragment);
    if (!noteElement) return { kind: 'none' };

    const { html, text } = extractNoteContent(noteElement);
    if (!text) return { kind: 'none' };

    return {
        kind: 'note',
        label: (anchor.textContent || '').trim(),
        html,
        text,
        anchor,
        fragment: noteTarget.fragment,
    };
}
